import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, Plus, Trash2, Pencil, ArrowLeft, ArrowRight, LogOut, Loader2, Lock, Sun,
  Check, Dumbbell, Activity, CalendarDays, Save, Droplets, LineChart, Scale, Utensils, RefreshCw, RotateCcw,
  Sparkles, Bookmark, Repeat, Search, Camera, Star, Info, Flag,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import BrandLockup from "@/components/BrandLockup";
import HeaderUtils from "@/components/HeaderUtils";
import PrimaryNav from "@/components/PrimaryNav";
import TimeField from "@/components/TimeField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { SPORTS, INTENSITY } from "@/lib/planModel";
import { litresLabel } from "@/lib/units";
import { toast } from "sonner";
import FoodLogDialog from "@/components/tracker/FoodLogDialog";
import PathwayMealEditDialog from "@/components/tracker/PathwayMealEditDialog";
import JournalSection from "@/components/JournalSection";
import TodayHero from "@/components/tracker/TodayHero";
import RaceWeekTips from "@/components/tracker/RaceWeekTips";
import AtAGlanceGrid from "@/components/tracker/AtAGlanceGrid";
import WeekComparisonChip from "@/components/WeekComparisonChip";
import WeeklyMicros from "@/components/wizard/WeeklyMicros";
import VitaminsReference from "@/components/wizard/VitaminsReference";
import { buildDaysFromTpEvents, diffTrainingWeek, addDaysISO } from "@/lib/tpSync";
import { computeRolledPlan } from "@/lib/weekRoll";
import { estimateKcal, tpSessionDone } from "@/lib/kcalEstimator";

const MEAL_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snacks" };
const MEALS = ["breakfast", "lunch", "dinner", "snack"];
// A day can repeat a slot (grazing style = 2 snacks), so each planned meal
// carries a unique key: "snack", "snack2". These helpers map a key back to
// its base slot and to a human label.
const baseSlot = (key) => String(key || "").replace(/\d+$/, "");
const slotLabel = (key) => {
  const n = String(key || "").match(/(\d+)$/);
  const label = MEAL_LABEL[baseSlot(key)] || baseSlot(key);
  if (!n) return label;
  return `${label === "Snacks" ? "Snack" : label} ${n[1]}`;
};
const SLOT_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack", fuel: "Training Fuel" };
const SPORT_LABEL = Object.fromEntries(SPORTS.map((s) => [s.value, s.label]));
const INTENSITY_LABEL = Object.fromEntries(INTENSITY.map((i) => [i.value, i.label]));
const QUICK_TAGS = ["Fuelling on point", "Under-fuelled", "Travel", "Eating out", "Low appetite", "Big appetite"];
const PAYLOAD_KEYS = ["name", "weekStart", "unitSystem", "goal", "profile", "dietary", "macros",
  "prevWeek", "trainingWeek", "nextWeek", "raceWeek", "selections", "completions", "generated", "currentStep"];

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseLocal = (iso) => { const [y, m, dd] = iso.split("-").map(Number); return new Date(y, m - 1, dd); };
const todayISO = () => toISO(new Date());
const addDays = (iso, n) => { const d = parseLocal(iso); d.setDate(d.getDate() + n); return toISO(d); };

// ------------------------- Small presentational bits -------------------------
// Shared Tracker section header — solid accent-colored square icon on the
// left (Whoop / Garmin style) so every collapsible tile reads with the same
// visual weight. Beta feedback (Feb 26 2026): the mixed inline-icon and
// bordered-icon look felt messy; a single "square CTA" pattern fixes that.
function Section({ icon: Icon, title, subtitle, accent = "#E2E30A", right, children, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-testid={testid} className="border border-white/10 bg-[#141414]">
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-4 px-4 sm:px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
        {Icon && (
          <span className="grid place-items-center h-12 w-12 shrink-0" style={{ backgroundColor: accent, boxShadow: `0 8px 24px -14px ${accent}` }}>
            <Icon className="h-6 w-6 text-black" strokeWidth={2.2} />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold uppercase tracking-[0.2em] text-white leading-tight break-words">{title}</span>
          {subtitle && <span className="block text-xs text-zinc-300 mt-1 leading-snug break-words">{subtitle}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-zinc-300 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-1">
          {right && <div className="mb-3 flex flex-wrap items-center gap-2">{right}</div>}
          {children}
        </div>
      )}
    </section>
  );
}

function Bar({ label, value, target, color }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : 0;
  const over = target && value > target;
  return (
    <div data-testid={`ring-${label.toLowerCase()}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="uppercase tracking-wider text-zinc-300">{label}</span>
        <span className="font-mono" style={{ color }}>{Math.round(value)}{target ? ` / ${Math.round(target)}` : ""}</span>
      </div>
      <div className="h-2 bg-white/10 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${target ? pct : 0}%`, background: over ? "#FF3B30" : color }} />
      </div>
    </div>
  );
}

// Vertical bars with an optional target marker line per bar.
function TrendBars({ data, valueKey, targetKey, color, onPick, activeDate, fmt }) {
  const max = Math.max(1, ...data.map((d) => Math.max(d[valueKey] || 0, (targetKey && d[targetKey]) || 0)));
  return (
    <div className="flex items-end gap-1 h-28" data-swipe-ignore>
      {data.map((d) => {
        const v = d[valueKey] || 0;
        const t = targetKey ? d[targetKey] : null;
        const h = Math.round((v / max) * 100);
        const th = t ? Math.round((t / max) * 100) : null;
        const over = t && v > t * 1.1;
        return (
          <button key={d.date} data-testid={`trend-${valueKey}-${d.date}`} onClick={() => onPick && onPick(d.date)}
            title={`${d.date}: ${fmt ? fmt(v) : v}`} className="flex-1 flex flex-col items-center gap-1 group min-w-0">
            <div className="w-full bg-white/5 h-20 relative flex items-end">
              <div className="w-full transition-all" style={{ height: `${h}%`, background: over ? "#FF3B30" : (d.date === activeDate ? "#E2E30A" : color) }} />
              {th != null && <div className="absolute left-0 right-0 h-px bg-white/60" style={{ bottom: `${th}%` }} />}
            </div>
            <span className="text-[8px] text-zinc-300">{d.label[0]}</span>
          </button>
        );
      })}
    </div>
  );
}

function WeightChart({ data }) {
  const pts = data.filter((d) => d.weight != null);
  if (pts.length < 2) return <p className="text-xs text-zinc-300">Log your weight on a few days to see your trend line here.</p>;
  const ws = pts.map((p) => p.weight);
  const min = Math.min(...ws), max = Math.max(...ws), range = max - min || 1;
  const W = 320, H = 90, padY = 10, padX = 6;
  const coords = pts.map((p, i) => {
    const x = padX + (i / (pts.length - 1)) * (W - padX * 2);
    const y = padY + (1 - (p.weight - min) / range) * (H - padY * 2);
    return [x, y];
  });
  const path = coords.map((c, i) => `${i ? "L" : "M"}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1], first = pts[0];
  const delta = (last.weight - first.weight).toFixed(1);
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-display text-2xl text-white">{last.weight}<span className="text-sm text-zinc-400"> kg</span></span>
        <span className={`text-xs font-mono ${delta > 0 ? "text-[#FF9500]" : "text-[#34C759]"}`}>{delta > 0 ? "+" : ""}{delta} kg</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none">
        <path d={path} fill="none" stroke="#4C9AFF" strokeWidth="2" />
        {coords.map((c, i) => <circle key={pts[i].date} cx={c[0]} cy={c[1]} r="2.5" fill="#E2E30A" />)}
      </svg>
    </div>
  );
}

// ------------------------------- Main page -------------------------------
export default function Tracker() {
  const navigate = useNavigate();
  const { logout, email, token, hasTracker, planTier, refreshUser, weightUnit, distanceUnit, planGoalWeight, user, progressPhotosEnabled, progressPhotosPrompted } = useAuth();
  const unitSystem = distanceUnit === "km" ? "metric" : "imperial";
  const isLite = planTier === "lite";
  const [date, setDate] = useState(todayISO());
  const [loading, setLoading] = useState(true);

  // Free data (any subscriber): plans (training + planned meals + completion), notes, TrainingPeaks.
  const [plans, setPlans] = useState([]);
  const emptyNote = { notes: "", tags: [], sessionFeedback: {}, hiddenMeals: [] };
  const [note, setNote] = useState(emptyNote);
  const [noteDirty, setNoteDirty] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [recap, setRecap] = useState([]);
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [tp, setTp] = useState({ connected: false, events: [] });
  const [tpUrl, setTpUrl] = useState("");
  const [tpBusy, setTpBusy] = useState(false);
  const [tpSyncing, setTpSyncing] = useState(false);
  const [tpResyncing, setTpResyncing] = useState(false);
  const [tpPaceBySport, setTpPaceBySport] = useState({});

  // Paid data (Tracker add-on): logged food, weight, history graphs.
  const [day, setDay] = useState(null);
  const [history, setHistory] = useState([]);
  const [dialog, setDialog] = useState({ open: false, meal: "breakfast", initialTab: undefined, initialBuildItems: undefined });
  const [quick, setQuick] = useState({ favourites: [], recents: [] });
  const [quickBusy, setQuickBusy] = useState(null);

  // Weekly micronutrient breakdown for the currently-matched plan. Older plans
  // (built before Weekly Micros shipped) don't carry a `micros` payload, so we
  // lazy-fetch it whenever the athlete opens the Tracker with such a plan.
  const [microsByPlan, setMicrosByPlan] = useState({});

  // ---- loaders ----
  const loadPlans = useCallback(async () => {
    try { const { data } = await api.get("/plans"); setPlans(data || []); }
    catch (e) { console.error("Tracker: loadPlans failed", e); }
  }, []);

  const loadTp = useCallback(async () => {
    try {
      const s = addDays(todayISO(), -60), e = addDays(todayISO(), 120);
      const { data } = await api.get(`/integrations/trainingpeaks/events?start=${s}&end=${e}`);
      setTp({ connected: !!data.connected, events: data.events || [] });
    } catch (e) { console.error("Tracker: loadTp failed", e); }
  }, []);

  const loadNote = useCallback(async (iso) => {
    try {
      const { data } = await api.get(`/journal/${iso}`);
      setNote({ notes: data.notes || "", tags: data.tags || [],
        sessionFeedback: data.sessionFeedback || {}, hiddenMeals: data.hiddenMeals || [] });
    }
    catch { setNote({ notes: "", tags: [], sessionFeedback: {}, hiddenMeals: [] }); }
    setNoteDirty(false);
  }, []);

  const loadDay = useCallback(async (iso) => {
    if (!hasTracker) return;
    try { const { data } = await api.get(`/tracker/day?date=${iso}`); setDay(data); }
    catch (e) { console.error("Tracker: loadDay failed", e); }
  }, [hasTracker]);

  const loadHistory = useCallback(async () => {
    if (!hasTracker) return;
    try { const { data } = await api.get("/tracker/history?days=21"); setHistory(data.days || []); }
    catch (e) { console.error("Tracker: loadHistory failed", e); }
  }, [hasTracker]);

  const loadQuick = useCallback(async () => {
    if (!hasTracker) return;
    try { const { data } = await api.get("/tracker/quicklog"); setQuick({ favourites: data.favourites || [], recents: data.recents || [] }); }
    catch (e) { console.error("Tracker: loadQuick failed", e); }
  }, [hasTracker]);

  const loadRecap = useCallback(async () => {
    try { const { data } = await api.get("/wellbeing/recap?days=14"); setRecap(data.days || []); }
    catch (e) { console.error("Tracker: loadRecap failed", e); }
  }, []);

  const loadProgressPhotos = useCallback(async () => {
    if (!user?.progressPhotosEnabled) { setProgressPhotos([]); return; }
    try { const { data } = await api.get("/tracker/progress-photos?limit=60"); setProgressPhotos(data.photos || []); }
    catch (e) { console.error("Tracker: loadProgressPhotos failed", e); }
  }, [user?.progressPhotosEnabled]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadPlans(), loadTp(), loadRecap()]);
      setLoading(false);
    })();
  }, [loadPlans, loadTp, loadRecap]);

  // Auto Week Roll — the FIRST time the tracker loads after Monday, silently
  // shift the plan's Next → This → Last so athletes never have to hit
  // "Use as this week". Regenerates macros against the promoted week and
  // persists once; guarded by a ref so it fires at most once per mount.
  const rollGuardRef = React.useRef(false);
  useEffect(() => {
    if (rollGuardRef.current || loading || !plans.length) return;
    const p = [...plans]
      .filter((x) => x.weekStart)
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))[0];
    if (!p) return;
    const { rolled, newPlan, gap } = computeRolledPlan(p);
    if (!rolled) return;
    rollGuardRef.current = true;
    (async () => {
      try {
        const cleanWeek = { ...(newPlan.trainingWeek || {}), days: (newPlan.trainingWeek?.days || []).map((d) => ({
          ...d, sessions: (d.sessions || []).filter((s) => (parseInt(s.duration, 10) || 0) > 0),
        })) };
        // Only regenerate if the promoted training week has something in it —
        // an empty week can keep the previous targets rather than reset to zero.
        let gen = newPlan.generated;
        const hasSessions = cleanWeek.days.some((d) => (d.sessions || []).length);
        if (hasSessions) {
          const res = await api.post("/engine/generate", {
            profile: newPlan.profile, dietary: newPlan.dietary, trainingWeek: cleanWeek,
            goal: newPlan.goal, macroOverride: newPlan.macros?.mode === "manual" ? newPlan.macros : null,
            raceWeek: newPlan.raceWeek,
          });
          gen = res.data;
        }
        const selections = {};
        (gen?.dayPathways || []).forEach((dp) => { selections[dp.index] = newPlan.selections?.[dp.index] || "A"; });
        const patch = { ...newPlan, trainingWeek: cleanWeek, generated: gen, selections };
        const body = {}; PAYLOAD_KEYS.forEach((k) => { if (patch[k] !== undefined) body[k] = patch[k]; });
        await api.put(`/plans/${p.id}`, body);
        await loadPlans();
        toast.success(gap === 1 ? "Rolled forward — this week is now your latest plan" : `Rolled forward ${gap} weeks — check your training`);
      } catch (err) { console.error("Auto week roll failed", err); }
    })();
  }, [loading, plans, loadPlans]);

  useEffect(() => { loadNote(date); loadDay(date); loadTp(); }, [date, loadNote, loadDay, loadTp]);
  // Re-fetch day targets when the athlete tweaks their plan hydration
  // baseline from Account Settings — `_live_daily` recomputes on the server,
  // this pulls the fresh number into the ring without needing a page reload.
  useEffect(() => { if (hasTracker) loadDay(date); }, [planGoalWeight, hasTracker, date, loadDay]);

  // Auto-refresh TrainingPeaks + recap every 5 minutes, and whenever the tab
  // regains focus. Athletes shouldn't need to hit a manual "Import from
  // TrainingPeaks" button — new completed sessions surface on their own within
  // a few minutes (the remaining wait is TP's own ~1-6h iCal CDN cache).
  useEffect(() => {
    const refresh = () => { loadPlans(); loadTp(); loadRecap(); if (hasTracker) loadDay(date); };
    const id = setInterval(refresh, 5 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [loadPlans, loadTp, loadRecap, loadDay, hasTracker, date]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadQuick(); }, [loadQuick]);
  useEffect(() => { loadProgressPhotos(); }, [loadProgressPhotos]);

  // Progress-Photos consent — ON by default (see AuthContext + user_public).
  // First time an athlete opens the Tracker after this feature ships we show
  // a one-time "want this on?" dialog. Their answer is persisted via
  // `progressPhotosPrompted=true` so the dialog never re-appears.
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  useEffect(() => {
    if (!user) return;
    if (progressPhotosPrompted) return;
    // Give the page a beat to mount so we don't stack on top of the
    // account-settings avatar prompt or any onboarding tour.
    const t = setTimeout(() => setConsentOpen(true), 1500);
    return () => clearTimeout(t);
  }, [user, progressPhotosPrompted]);
  const answerConsent = async (keepOn) => {
    if (consentBusy) return;
    setConsentBusy(true);
    try {
      await api.post("/users/profile-defaults", {
        progressPhotos: !!keepOn,
        progressPhotosPrompted: true,
      });
      await refreshUser();
      const { toast } = await import("sonner");
      toast.success(keepOn
        ? "Progress photos on — tile added to Daily Snapshot"
        : "Progress photos off — you can turn it on any time in Account Settings");
      setConsentOpen(false);
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err?.response?.data?.detail || "Could not save preference");
    } finally { setConsentBusy(false); }
  };

  // ---- plan/day matching (free training + planned meals) ----
  const match = useMemo(() => {
    // Prefer a plan whose weekStart..weekStart+6 window COVERS the viewed
    // date — beta feedback (Feb 2026): "went back through last week's
    // training it didn't end up showing, so had to flick onto the previous
    // plan, then regenerate, then when it showed had to go onto current
    // week and do the same". Root cause was always picking the latest
    // plan and mapping today's weekday onto it. Now we auto-load the
    // right saved week when the athlete steps back through dates.
    // If no plan explicitly covers the date, fall back to the most-recent
    // plan as a repeating template (matches the original behaviour and the
    // backend's live daily targets logic).
    const usable = plans.filter((p) => p.generated?.dayPathways || p.mealPlanning === false || p.generated?.targets);
    if (!usable.length) return null;
    const iso = date;
    const covering = usable.filter((p) => {
      if (!p.weekStart) return false;
      const ws = p.weekStart;
      const we = (() => {
        const d = new Date(`${ws}T00:00:00`);
        d.setDate(d.getDate() + 6);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      })();
      return iso >= ws && iso <= we;
    });
    let plan;
    let idx;
    let isCovering;
    if (covering.length) {
      plan = covering.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))[0];
      idx = Math.floor((new Date(`${iso}T00:00:00`) - new Date(`${plan.weekStart}T00:00:00`)) / 86400000);
      isCovering = true;
    } else {
      plan = [...usable].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))[0];
      idx = (parseLocal(date).getDay() + 6) % 7;
      isCovering = false;
    }
    return { plan, idx, covering: isCovering };
  }, [plans, date]);

  const hiddenMeals = note.hiddenMeals || [];
  const planDay = match && match.plan.generated?.dayPathways
    ? (match.plan.generated.dayPathways.find((dp) => dp.index === match.idx) || match.plan.generated.dayPathways[match.idx])
    : null;
  const sel = match ? (match.plan.selections?.[match.idx] || "A") : "A";
  const allPlannedMeals = isLite ? [] : (() => {
    const seen = {};
    return (planDay?.pathways?.[sel]?.meals || []).map((m) => {
      const s = m.slot;
      const occ = (seen[s] = (seen[s] || 0) + 1);
      // Legacy plans (saved before slotKey existed) get keys derived here so
      // repeated slots never collapse into one card.
      return { ...m, slotKey: m.slotKey || (occ === 1 ? s : `${s}${occ}`) };
    });
  })();
  const plannedMeals = allPlannedMeals.filter((m) => !hiddenMeals.includes(m.slotKey));
  const removedPlanned = allPlannedMeals.filter((m) => hiddenMeals.includes(m.slotKey));
  // Sessions from the plan's trainingWeek — ONLY show them when the date
  // is inside the plan's actual weekStart..+6 window. For future dates
  // that fall back to the template, we return an empty array so the
  // Tracker doesn't parade phantom "Running 2h 7m" sessions that don't
  // exist in TrainingPeaks (beta feedback Feb 27 2026).
  const sessions = match && match.covering ? (match.plan.trainingWeek?.days?.[match.idx]?.sessions || []) : [];
  const completions = match ? (match.plan.completions || {}) : {};
  const doneCount = plannedMeals.filter((m) => completions[`${match?.idx}:${m.slotKey}`]).length;
  const tpToday = tp.events.filter((e) => e.date === date);

  // ---- micros for the currently-matched plan --------------------------------
  const activePlan = match?.plan;
  const cachedPlanMicros = activePlan?.generated?.micros;
  // Detect payloads saved before the "Top-ups needed" fields (food/gap) were
  // added, so plans generated by the older backend get transparently upgraded.
  const microsIsStale = cachedPlanMicros && cachedPlanMicros.nudges?.length && !("food" in cachedPlanMicros.nudges[0]);
  const micros = activePlan
    ? (microsIsStale ? (microsByPlan[activePlan.id] || null) : (cachedPlanMicros || microsByPlan[activePlan.id] || null))
    : null;
  useEffect(() => {
    if (!activePlan) return;
    const hasFresh = cachedPlanMicros && !microsIsStale;
    if (hasFresh || microsByPlan[activePlan.id]) return;
    if (!activePlan.generated?.dayPathways) return;
    (async () => {
      try {
        const sel2 = {};
        Object.keys(activePlan.selections || {}).forEach((k) => { sel2[String(k)] = activePlan.selections[k]; });
        const { data } = await api.post("/engine/micros", {
          dayPathways: activePlan.generated.dayPathways, selections: sel2, profile: activePlan.profile || {},
        });
        if (data?.micros) setMicrosByPlan((m) => ({ ...m, [activePlan.id]: data.micros }));
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Tracker micros backfill failed", e?.message || e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlan?.id, microsIsStale]);

  // ---- actions ----
  // Beta feedback (Feb 2026, round 3): "I need those ingredients in my
  // log so I can substitute them or change weights individually."
  // ✓ Done for a planned meal now batch-logs each ingredient as its
  // own editable row (source="pathway"). Toggling off deletes those
  // auto-created rows. Fuel slot keeps the light virtual-tick behaviour
  // since it's a single drink/gel, not a multi-ingredient plate.
  const toggleMeal = async (slot) => {
    if (!match) return;
    const key = `${match.idx}:${slot}`;
    const p = match.plan;
    const c = { ...(p.completions || {}) };
    const nowOn = !c[key];
    if (c[key]) delete c[key]; else c[key] = true;

    // Auto-log / auto-remove ingredient rows on tick for planned pathway meals.
    if (hasTracker && slot !== "fuel") {
      const planned = plannedMeals.find((m) => m.slotKey === slot);
      const existing = (day?.entries || []).filter((e) => e.meal === slot);
      if (nowOn && planned && existing.length === 0) {
        const items = Array.isArray(planned.items) ? planned.items : [];
        const anyMacros = items.some((it) => (it.kcal || 0) > 0 || (it.protein || 0) > 0 || (it.carb || 0) > 0 || (it.fat || 0) > 0);
        if (items.length && anyMacros) {
          const payloadItems = items.map((it) => ({
            name: (it.name || planned.name || "Ingredient").trim(),
            qty: Number(it.qty || 1),
            unit: it.unit || "serving",
            kcal: Math.round(it.kcal || 0),
            protein: Math.round((it.protein || 0) * 10) / 10,
            carb: Math.round((it.carb || 0) * 10) / 10,
            fat: Math.round((it.fat || 0) * 10) / 10,
          }));
          try {
            await api.post("/tracker/log/batch", {
              date, meal: slot, items: payloadItems,
              source: "pathway", recipe_name: planned.name || null,
            });
            toast.success(`Logged ${payloadItems.length} ingredients — edit any row to swap or adjust weight`);
          } catch { toast.error("Could not log ingredients — try again"); }
        }
      } else if (!nowOn && planned) {
        const toDelete = existing.filter((e) => e.source === "pathway" && (!e.recipe_name || e.recipe_name === planned.name));
        for (const row of toDelete) {
          try { await api.delete(`/tracker/log/${row.id}`); } catch {}
        }
      }
    }

    const updated = { ...p, completions: c };
    setPlans((list) => list.map((x) => (x.id === p.id ? updated : x)));
    try {
      const payload = {}; PAYLOAD_KEYS.forEach((k) => { payload[k] = updated[k]; });
      await api.put(`/plans/${p.id}`, payload);
      if (hasTracker) { loadDay(date); loadHistory(); }
    } catch { toast.error("Could not save — try again"); setPlans((list) => list.map((x) => (x.id === p.id ? p : x))); }
  };

  // ---- meal swapping (edit the planned meal for a slot, rebalanced to target) ----
  const [swapCtx, setSwapCtx] = useState(null); // { slot, meal }
  const [swapAlts, setSwapAlts] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapping, setSwapping] = useState(false);

  const openMealSwap = async (slot, meal) => {
    if (!match || !meal) return;
    setSwapCtx({ slot, meal });
    setSwapAlts([]); setSwapLoading(true);
    try {
      const usedIds = plannedMeals.map((m) => m.id);
      const { data } = await api.post("/engine/meal-alternatives", { dietary: match.plan.dietary || {}, slot: baseSlot(slot), excludeIds: usedIds });
      setSwapAlts(data.alternatives || []);
    } catch { toast.error("Couldn't load alternatives — please try again"); }
    finally { setSwapLoading(false); }
  };

  const doMealSwap = async (newMealId) => {
    if (!match || !swapCtx) return;
    setSwapping(true);
    const p = match.plan; const idx = match.idx;
    try {
      const { data } = await api.post("/engine/swap-meal", {
        dietary: p.dietary || {},
        dayTarget: planDay.target,
        currentMeals: plannedMeals,
        slot: swapCtx.slot,
        newMealId,
      });
      const nextDayPathways = p.generated.dayPathways.map((d) =>
        d.index !== idx ? d : { ...d, pathways: { ...d.pathways, [sel]: { ...data } } });
      const updated = { ...p, generated: { ...p.generated, dayPathways: nextDayPathways } };
      setPlans((list) => list.map((x) => (x.id === p.id ? updated : x)));
      const payload = {}; PAYLOAD_KEYS.forEach((k) => { payload[k] = updated[k]; });
      await api.put(`/plans/${p.id}`, payload);
      toast.success("Meal swapped — portions rebalanced to hit your target");
      setSwapCtx(null);
    } catch { toast.error("Swap failed — please try again"); }
    finally { setSwapping(false); }
  };

  const toggleTag = (t) => { setNote((n) => ({ ...n, tags: n.tags.includes(t) ? n.tags.filter((x) => x !== t) : [...n.tags, t] })); setNoteDirty(true); };
  const updateNote = (partial) => { setNote((n) => ({ ...n, ...partial })); setNoteDirty(true); };
  const notePayload = (n) => ({
    notes: n.notes, tags: n.tags, sessionFeedback: n.sessionFeedback,
    hiddenMeals: n.hiddenMeals || [],
  });
  const saveNote = async () => {
    setSavingNote(true);
    try { await api.put(`/journal/${date}`, notePayload(note)); setNoteDirty(false); toast.success("Journal saved"); loadRecap(); }
    catch { toast.error("Could not save journal"); }
    finally { setSavingNote(false); }
  };

  // Beta feedback (Feb 2026): "button to save training comments, constantly
  // forgetting to go to morning log and save!" — every edit inside the
  // Morning Report / Training / Wellness cards is now auto-saved 1.8s
  // after the last keystroke or tap. The explicit Save button stays as
  // a visible safety net; this just removes the frustration of losing
  // notes when the athlete forgets to press it.
  const autoSaveRef = useRef(null);
  useEffect(() => {
    if (!noteDirty) return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      // Silent version of saveNote — no toast so the athlete isn't
      // interrupted every 2s while typing. Manual Save still toasts.
      (async () => {
        setSavingNote(true);
        try { await api.put(`/journal/${date}`, notePayload(note)); setNoteDirty(false); loadRecap(); }
        catch { /* silent — athlete can still hit Save manually */ }
        finally { setSavingNote(false); }
      })();
    }, 1800);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note, noteDirty, date]);

  const persistNote = async (next) => {
    setNote(next);
    try { await api.put(`/journal/${date}`, notePayload(next)); loadDay(date); }
    catch { toast.error("Could not update — please try again"); }
  };
  const removePlannedMeal = (slot) => {
    const hidden = note.hiddenMeals || [];
    if (hidden.includes(slot)) return;
    persistNote({ ...note, hiddenMeals: [...hidden, slot] });
    toast.success("Planned meal removed for today");
  };
  const restorePlannedMeal = (slot) => {
    persistNote({ ...note, hiddenMeals: (note.hiddenMeals || []).filter((s) => s !== slot) });
    toast.success("Planned meal restored");
  };

  const connectTp = async () => {
    if (!tpUrl.trim()) return;
    setTpBusy(true);
    try { const { data } = await api.put("/integrations/trainingpeaks", { url: tpUrl.trim() }); toast.success(`Connected — ${data.count} workouts found`); setTpUrl(""); await loadTp(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not connect that calendar"); }
    finally { setTpBusy(false); }
  };
  const disconnectTp = async () => {
    try { await api.delete("/integrations/trainingpeaks"); setTp({ connected: false, events: [] }); toast.success("TrainingPeaks disconnected"); }
    catch { toast.error("Could not disconnect"); }
  };
  const refreshTp = async () => {
    setTpSyncing(true);
    try { await loadTp(); await loadRecap(); toast.success("Synced with TrainingPeaks"); }
    catch { toast.error("Could not sync — try again"); }
    finally { setTpSyncing(false); }
  };

  // ---- TrainingPeaks change detection ----
  // Compare the plan's saved training week against the live TP events for the
  // same 7-day window. If any day's session count or total minutes differ, we
  // surface a banner in the Tracker's Training card offering a one-tap
  // re-import + recalc.
  const plan = match?.plan;
  const tpDiff = useMemo(() => {
    if (!tp.connected || !plan?.weekStart) return { changed: false, changedDays: [] };
    const ws = plan.weekStart;
    const weekEvents = tp.events.filter((e) => {
      const d = e.date;
      return d >= ws && d <= addDaysISO(ws, 6);
    });
    if (!weekEvents.length && !(plan.trainingWeek?.days || []).some((d) => (d.sessions || []).length)) {
      return { changed: false, changedDays: [] };
    }
    const liveDays = buildDaysFromTpEvents(weekEvents, ws, tpPaceBySport);
    return diffTrainingWeek(plan.trainingWeek?.days || [], liveDays);
  }, [tp.connected, tp.events, plan, tpPaceBySport]);

  const resyncFromTp = async () => {
    if (!plan?.weekStart) return;
    setTpResyncing(true);
    try {
      const start = plan.weekStart;
      const { data: evData } = await api.get(`/integrations/trainingpeaks/events?start=${start}&end=${addDaysISO(start, 6)}`);
      if (!evData.connected) { toast.error("Connect TrainingPeaks first"); return; }
      const newDays = buildDaysFromTpEvents(evData.events || [], start, evData.paceBySport || {});
      const cleanWeek = { ...(plan.trainingWeek || {}), days: newDays.map((d) => ({
        ...d, sessions: (d.sessions || []).filter((s) => (parseInt(s.duration, 10) || 0) > 0),
      })) };
      const { data: gen } = await api.post("/engine/generate", {
        profile: plan.profile, dietary: plan.dietary, trainingWeek: cleanWeek,
        goal: plan.goal, macroOverride: plan.macros?.mode === "manual" ? plan.macros : null,
        raceWeek: plan.raceWeek,
      });
      const selections = {};
      gen.dayPathways.forEach((dp) => { selections[dp.index] = plan.selections?.[dp.index] || "A"; });
      const patch = { ...plan, trainingWeek: cleanWeek, generated: gen, selections };
      const body = {}; PAYLOAD_KEYS.forEach((k) => { if (patch[k] !== undefined) body[k] = patch[k]; });
      await api.put(`/plans/${plan.id}`, body);
      await Promise.all([loadPlans(), loadTp()]);
      toast.success("Training re-imported — targets recalculated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not re-import from TrainingPeaks"); }
    finally { setTpResyncing(false); }
  };

  // ---- Auto-resync when TrainingPeaks has drifted ----
  // The moment TP diffs from what's saved in the plan (e.g. an unplanned
  // workout got logged, a session was moved, or a race was added), we
  // silently re-import + recompute so targets always match live training.
  // No manual "re-import" tap required. Guarded by a ref so we fire at
  // most once per detected diff and never mid-flight.
  const autoResyncRef = useRef(null);
  useEffect(() => {
    if (!tpDiff.changed) return;
    if (tpResyncing) return;
    if (!plan?.id) return;
    // Fingerprint the diff so we only fire once per unique change set.
    const sig = plan.id + "|" + (tpDiff.changedDays || []).sort().join(",");
    if (autoResyncRef.current === sig) return;
    autoResyncRef.current = sig;
    const t = setTimeout(() => { resyncFromTp(); }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpDiff, tpResyncing, plan?.id]);

  const removeLog = async (id) => {
    try { await api.delete(`/tracker/log/${id}`); toast.success("Removed"); loadDay(date); loadHistory(); loadQuick(); }
    catch { toast.error("Could not remove entry"); }
  };

  // Edit a logged entry — prompt for a new quantity (and, if the entry is
  // "1 serving", macros will re-scale server-side proportionally). Cheap,
  // reliable and doesn't need a whole portion-picker dialog. If the athlete
  // hits Cancel or types garbage we bail out silently.
  const [editEntry, setEditEntry] = useState(null);
  const [swapEntryCtx, setSwapEntryCtx] = useState(null); // logged entry to swap
  const editLog = (entry) => setEditEntry(entry);

  // Beta feedback (Feb 2026, round 2): "Keep the pathway choice as my
  // 'guide' and let LOG log what I ACTUALLY ate as a completely separate
  // item." Previously LOG opened PathwayMealEditDialog pre-loaded with
  // pathway ingredients — great for tweaking, wrong for free-form logging.
  // New behaviour:
  //   • Yellow LOG button on every meal → opens fresh FoodLogDialog
  //     (search / barcode / photo / build) — no pathway coupling.
  //   • Pathway stays visible below as the planned guide with its ✓ Done,
  //     Swap and Remove controls unchanged.
  //   • A small "Edit ingredients & log" link inside the Planned box
  //     preserves the pathway-edit flow for athletes who want it.
  const [pathwayEdit, setPathwayEdit] = useState(null); // { slot, planned }
  const saveEditEntry = async (patch) => {
    if (!editEntry) return;
    try {
      await api.patch(`/tracker/log/${editEntry.id}`, patch);
      toast.success("Updated");
      setEditEntry(null);
      loadDay(date); loadHistory();
    } catch { toast.error("Could not update entry"); }
  };

  // Row-level ingredient swap: replaces a logged food with a fresh search
  // pick, preserving the meal slot and (for weight-based rows) the grams so
  // athletes can drop-in-replace "Oat milk 310ml" with "Semi-skimmed 310ml".
  const swapEntryToFood = async (food) => {
    if (!swapEntryCtx) return;
    const isWeight = swapEntryCtx.unit === "g" || swapEntryCtx.unit === "ml";
    const grams = isWeight ? Number(swapEntryCtx.qty || 100) : 100;
    const s = (per100) => ((per100 || 0) * grams) / 100;
    try {
      await api.patch(`/tracker/log/${swapEntryCtx.id}`, {
        name: food.name,
        qty: grams,
        unit: swapEntryCtx.unit === "ml" ? "ml" : "g",
        kcal: Math.round(s(food.kcal)),
        protein: Math.round(s(food.protein) * 10) / 10,
        carb: Math.round(s(food.carb) * 10) / 10,
        fat: Math.round(s(food.fat) * 10) / 10,
        source: "search",
      });
      toast.success(`Swapped for ${food.name}`);
      setSwapEntryCtx(null);
      loadDay(date); loadHistory();
    } catch { toast.error("Could not swap — try again"); }
  };

  const quickLog = async (food, key, mealOverride) => {
    setQuickBusy(key);
    try {
      await api.post("/tracker/log", { date, meal: mealOverride || food.meal || "snack", name: food.name,
        qty: food.qty, unit: food.unit, kcal: food.kcal, protein: food.protein,
        carb: food.carb, fat: food.fat, source: food.source || "quick" });
      toast.success(`Logged ${food.name} → ${mealOverride || food.meal || "snack"}`);
      loadDay(date); loadHistory(); loadQuick();
    } catch { toast.error("Could not log — please try again"); }
    finally { setQuickBusy(null); }
  };

  const toggleFav = async (food, isFav) => {
    try {
      if (isFav) { await api.delete(`/tracker/favourites/${food.id}`); toast.success("Removed favourite"); }
      else {
        await api.post("/tracker/favourites", { name: food.name, meal: food.meal,
          qty: food.qty, unit: food.unit, kcal: food.kcal, protein: food.protein,
          carb: food.carb, fat: food.fat, source: food.source });
        toast.success("Saved to favourites");
      }
      loadQuick();
    } catch { toast.error("Could not update favourites"); }
  };

  // ---- derived (paid) ----
  const target = day?.target;
  const consumed = day?.consumed || { kcal: 0, protein: 0, carb: 0, fat: 0 };
  const entriesByMeal = (m) => (day?.entries || []).filter((e) => e.meal === m);
  const week = history.slice(-7);
  const onTargetDays = week.filter((h) => h.target && h.kcal >= h.target * 0.9 && h.kcal <= h.target * 1.1).length;
  const todayTrainMin = (history.find((h) => h.date === date)?.trainingMin) || 0;
  const adherencePct = week.length ? Math.round((onTargetDays / 7) * 100) : 0;
  const plannedBySlot = {}; plannedMeals.forEach((m) => { plannedBySlot[m.slotKey] = m; });
  // Render order: always breakfast → lunch → dinner → snacks, regardless of the
  // order the plan happens to list them in. Repeated slots (snack, snack2) keep
  // their numeric sequence, and standard slots with nothing planned still show
  // so athletes can log against them.
  const mealSlots = [
    ...plannedMeals.map((m) => m.slotKey).filter((k) => baseSlot(k) !== "fuel"),
    ...MEALS.filter((s) => !plannedMeals.some((m) => m.slotKey === s)),
  ].sort((a, b) => {
    const ai = MEALS.indexOf(baseSlot(a)), bi = MEALS.indexOf(baseSlot(b));
    if (ai !== bi) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    const num = (k) => Number((String(k).match(/(\d+)$/) || [, "1"])[1]);
    return num(a) - num(b);
  });
  const loggedMeals = new Set((day?.entries || []).map((e) => e.meal));
  // Backend now folds ticked-off planned meals into `consumed`; this flag only drives the hint.
  const tickedContributes = !!match && ["breakfast", "lunch", "dinner", "snack", "fuel"].some(
    (slot) => plannedBySlot[slot] && completions[`${match.idx}:${slot}`] && (slot === "fuel" || !loggedMeals.has(slot)));
  const weighed = history.filter((h) => h.weight != null);
  const lastWeight = weighed.length ? weighed[weighed.length - 1].weight : (day?.weight?.kg ?? null);
  const prevWeight = weighed.length > 1 ? weighed[weighed.length - 2].weight : null;
  const weightDelta = lastWeight != null && prevWeight != null ? +(lastWeight - prevWeight).toFixed(1) : null;

  const isToday = date === todayISO();
  const labelLong = parseLocal(date).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  const weekStrip = useMemo(() => {
    const dow = (parseLocal(date).getDay() + 6) % 7;
    const monday = addDays(date, -dow);
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [date]);

  // Base rest-day target + planned training fuel + fuel earned via completed
  // sessions (manual ticks OR TrainingPeaks auto-completions). Powers the
  // dual-ring inner/outer ring in TodayHero: inner = base, outer = base +
  // earned fuel. Beta feedback (Feb 26 2026): 2/2 sessions were DONE via
  // TP but the hero showed "FUEL 0/460 — unlock once you complete today's
  // training". Root cause: `completions` only reflects manual ticks; TP
  // auto-completed sessions live in `tpToday[i].completed`. We now credit
  // fuel from EITHER source so autoDone workouts unlock the training fuel
  // just like a manual tick.
  const dailyRow = match?.plan?.generated?.targets?.daily?.[match.idx];
  // Guard: rest days (no planned training minutes AND no active sessions
  // today) should never show a planned-fuel budget. Beta feedback Sep 2026
  // screenshot: rest day was showing "+FUEL 0/2054" from a stale carry-over
  // in the plan's `supportedKcal` row. Nothing to earn = show nothing.
  const dayHasTraining = ((dailyRow?.trainingMinutes || 0) > 0) || sessions.length > 0;
  const plannedFuelKcal = dayHasTraining ? Math.round(dailyRow?.supportedKcal || 0) : 0;
  const baseTarget = Math.max(0, Math.round((target?.energy || 0) - plannedFuelKcal));
  const totalSessions = sessions.length;
  const tpUsedForFuel = new Set();
  const completedSessions = totalSessions
    ? sessions.filter((s, i) => {
        if (completions[`${match?.idx}:session:${i}`]) return true;
        if (completions[`${match?.idx}:fuel`]) return true;
        const tpIdx = (tpToday || []).findIndex((t, ti) => !tpUsedForFuel.has(ti) && t.sport === s.sport && tpSessionDone(t));
        if (tpIdx >= 0) { tpUsedForFuel.add(tpIdx); return true; }
        return false;
      }).length
    : 0;
  // Planned minutes for the day (used to work out each session's share of
  // the day's fuel when an athlete ticks one off manually).
  const plannedMin = sessions.reduce((s, x) => s + (x.duration || 0), 0);
  // Total kcal burnt across today's completed TP sessions — includes the
  // MET fallback estimator so feeds without native `caloriesBurned` still
  // credit a number (identical to what JournalSection shows on each card).
  // Surfaced in TodayHero so athletes see the training burn AND understand
  // it already powers their +Fuel top-up (rather than being subtracted
  // from what they eat). Beta feedback Feb 26 2026.
  const athleteWeightKg = match?.plan?.profile?.weight || day?.weight?.kg || null;
  const burnedKcal = (tpToday || [])
    .filter((t) => tpSessionDone(t))
    .reduce((s, t) => {
      if (t.caloriesBurned) return s + t.caloriesBurned;
      const est = estimateKcal({ sport: t.sport, durationMin: t.durationMin, weightKg: athleteWeightKg });
      return s + (est || 0);
    }, 0);
  // ⚡ Fuel is credited from ACTUAL WORK DONE, never from session count
  // (user report June 2026: ticking off a 35m walk that TP shows as 197 kcal
  // credited 421 kcal — half the day's fuel — because it was "1 of 2
  // sessions"). Now:
  //   • sessions completed in TrainingPeaks earn their real burn × support share
  //   • sessions ticked manually with no TP activity earn their planned
  //     duration's share of the day's fuel
  // Nothing is capped at the planned figure, so a session that went bigger
  // than planned (e.g. a half marathon) still earns the real work done.
  const manualOnlyMin = sessions.reduce((sum, s, i) => {
    const ticked = !!(completions[`${match?.idx}:session:${i}`] || completions[`${match?.idx}:fuel`]);
    const tpMatched = (tpToday || []).some((t) => tpSessionDone(t) && t.sport === s.sport);
    return ticked && !tpMatched ? sum + (s.duration || 0) : sum;
  }, 0);
  const manualShareKcal = plannedMin > 0
    ? Math.round(plannedFuelKcal * Math.min(1, manualOnlyMin / plannedMin))
    : (manualOnlyMin > 0 ? plannedFuelKcal : 0);
  const burnBasedEarned = Math.round((burnedKcal || 0) * 0.85);
  const earnedFuelKcal = burnBasedEarned + manualShareKcal;

  // ---- Mobile swipe: left/right on the tracker body steps a day ----
  // Ignores swipes that start inside dialogs, inputs, textareas or any
  // element opted-out with `data-swipe-ignore` (e.g. horizontally-
  // scrolling strips) so we don't hijack legitimate interactions.
  const touchRef = useRef({ x: 0, y: 0, t: 0, ignore: false });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    const target = e.target;
    const ignore = !!(target.closest && target.closest(
      '[role="dialog"], input, textarea, select, [data-swipe-ignore], [contenteditable="true"]'));
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), ignore };
  };
  const onTouchEnd = (e) => {
    if (touchRef.current.ignore) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    if (Math.abs(dx) < 60 || Math.abs(dy) > 40 || dt > 600) return;
    setDate(addDays(date, dx < 0 ? 1 : -1));
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <BrandLockup />
          <div className="ml-auto shrink-0"><HeaderUtils /></div>
        </div>
        <PrimaryNav plans={plans} />
      </header>

      <main data-testid="tracker-page" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Dialog open={consentOpen} onOpenChange={(o) => { if (!o) setConsentOpen(false); }}>
          <DialogContent data-testid="progress-photos-consent" className="force-dark bg-[#141414] border-white/15 text-white sm:max-w-md rounded-none p-0 gap-0">
            <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/10">
              <DialogTitle className="text-xs font-bold uppercase tracking-[0.22em] text-white flex items-center gap-2">
                <Camera className="h-4 w-4" style={{ color: "#FB7185" }} /> Progress photos
              </DialogTitle>
              <DialogDescription className="sr-only">Choose whether progress photos appear in your Daily Snapshot.</DialogDescription>
            </DialogHeader>
            <div className="p-5 space-y-4">
              <p className="text-sm text-zinc-100 leading-relaxed">
                We can add a <b className="text-white">Progress Photo</b> tile
                to your Daily Snapshot so you can log body/progress shots and
                see side-by-side before / after with your weight and
                7-day average overlaid.
              </p>
              <ul className="text-[11px] text-zinc-300 space-y-1.5 leading-relaxed">
                <li>• <b className="text-white">Private by design</b> — only you (and your coach, if you're linked to one) can see them. Not even SPC Performance Nutrition admins can view your photos.</li>
                <li>• Pose tags (front / side / back / custom) so before-after always matches.</li>
                <li>• Turn on or off any time in Account Settings → Wellness.</li>
              </ul>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button type="button" data-testid="progress-photos-consent-off"
                  onClick={() => answerConsent(false)} disabled={consentBusy}
                  className="h-11 border border-white/15 hover:border-white/40 text-white text-[11px] uppercase font-bold tracking-wider transition-colors inline-flex items-center justify-center">
                  {consentBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "No thanks"}
                </button>
                <button type="button" data-testid="progress-photos-consent-on"
                  onClick={() => answerConsent(true)} disabled={consentBusy}
                  className="h-11 bg-[#FB7185] hover:bg-[#e85e73] disabled:opacity-60 text-black text-[11px] uppercase font-bold tracking-wider inline-flex items-center justify-center gap-1.5">
                  {consentBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Keep it on"}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Date navigator — single ‹ / › steps a day, double « / » steps a week */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-1">
            <button data-testid="week-prev" onClick={() => setDate(addDays(date, -7))} title="Previous week"
              className="p-2 border border-white/15 hover:border-[#E2E30A]/50 transition-colors"><ChevronsLeft className="h-4 w-4" /></button>
            <button data-testid="date-prev" onClick={() => setDate(addDays(date, -1))} title="Previous day"
              className="p-2 border border-white/15 hover:border-[#E2E30A]/50 transition-colors"><ChevronLeft className="h-4 w-4" /></button>
          </div>
          <div className="text-center">
            <p className="font-display text-lg sm:text-xl uppercase tracking-tight" data-testid="tracker-date">{labelLong}</p>
            {!isToday && <button data-testid="date-today" onClick={() => setDate(todayISO())} className="text-[10px] uppercase tracking-wider text-[#4C9AFF] hover:underline">Jump to today</button>}
            {isToday && <p className="text-[10px] uppercase tracking-widest text-[#E2E30A]">Today</p>}
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="date-next" onClick={() => setDate(addDays(date, 1))} title="Next day"
              className="p-2 border border-white/15 hover:border-[#E2E30A]/50 transition-colors"><ChevronRight className="h-4 w-4" /></button>
            <button data-testid="week-next" onClick={() => setDate(addDays(date, 7))} title="Next week"
              className="p-2 border border-white/15 hover:border-[#E2E30A]/50 transition-colors"><ChevronsRight className="h-4 w-4" /></button>
          </div>
        </div>

        {/* One-tap TP diagnostic — copies parsed events + raw ICS description
            for the current day to the clipboard when TP is connected. Lets
            athletes tell us exactly what label their feed uses when the
            calories/HR/TSS cards don't show. */}
        

        {/* Week strip */}
        <div className="grid grid-cols-7 gap-1 mb-6" data-swipe-ignore>
          {weekStrip.map((iso) => {
            const active = iso === date, isTod = iso === todayISO();
            return (
              <button key={iso} data-testid={`cal-strip-${iso}`} onClick={() => setDate(iso)}
                className={`py-2 text-center border transition-colors ${active ? "border-[#E2E30A] bg-[#E2E30A]/10" : "border-white/10 hover:border-white/30"}`}>
                <span className="block text-[9px] uppercase tracking-wider text-zinc-400">{parseLocal(iso).toLocaleDateString(undefined, { weekday: "short" })}</span>
                <span className={`block text-sm font-mono ${active ? "text-[#E2E30A]" : isTod ? "text-[#4C9AFF]" : "text-white"}`}>{parseLocal(iso).getDate()}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#004AAD]" /></div>
        ) : (
          <div className="space-y-5">
            {/* No plan covers the selected date — surface basic-only mode
                so the athlete knows why the numbers look bare (Feb 27 2026
                beta feedback: "if user selects three weeks on tracker say
                basic calories only, no active calories used as plan not
                built yet"). */}
            {hasTracker && (!match || !match.covering) && (
              <div data-testid="tracker-no-plan-notice"
                className="border border-[#4C9AFF]/40 bg-[#4C9AFF]/[0.08] px-4 py-3 flex items-start gap-3">
                <Info className="h-4 w-4 text-[#4C9AFF] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#4C9AFF]">Basic calories only</p>
                  <p className="text-[11px] text-zinc-100 mt-1 leading-relaxed">
                    No plan built for this week yet — showing maintenance calories with no training top-up. Head to the plan builder to generate a fuelling plan for this week, then targets, macros and race carb-loading will light up automatically.
                  </p>
                  <button type="button" data-testid="tracker-no-plan-cta"
                    onClick={() => navigate("/app")}
                    className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#4C9AFF] hover:text-white transition-colors">
                    Build plan for this week →
                  </button>
                </div>
              </div>
            )}
            {hasTracker && <TodayHero date={date} consumed={consumed} target={target} trainMin={todayTrainMin} baseTarget={baseTarget} plannedFuelKcal={plannedFuelKcal} earnedFuelKcal={earnedFuelKcal} burnedKcal={burnedKcal}
              sessionsDone={completedSessions} sessionsTotal={totalSessions}
              hasTrainingToday={sessions.length > 0 || (tpToday && tpToday.length > 0) || (dailyRow?.trainingMinutes || 0) > 0}
              plannedTrainMin={Math.max(dailyRow?.trainingMinutes || 0, sessions.reduce((s, x) => s + (x.duration || 0), 0), (tpToday || []).reduce((s, t) => s + (t.durationMin || 0), 0))}
              isRace={!!(dailyRow?.isRace) || sessions.some((s) => (s.intensity || "").toLowerCase() === "race" || s.isRace)}
              carbLoadBumpGkg={dailyRow?.carbLoadBumpGkg || 0}
              daysUntilRace={dailyRow?.daysUntilRace ?? null}
              carbTarget={target?.carb || 0}
              carbLoadKcal={Math.round((dailyRow?.carbLoadBumpGkg || 0) * (match?.plan?.profile?.weight || 0) * 4)}
            />}

            {/* Race-week decision helper — replaced by a Race Hub shortcut
                on race-week days (Feb 27 2026). RaceWeekTips still renders
                on T-3 → T-0 when the Race Hub isn't reachable (rare) but
                by default we point the athlete to the dedicated hub. */}
            {hasTracker && ((dailyRow?.daysUntilRace ?? null) !== null && dailyRow?.daysUntilRace >= 0 && dailyRow?.daysUntilRace <= 3 || dailyRow?.isRace) && (
              <button type="button" onClick={() => navigate("/race")}
                data-testid="race-hub-shortcut"
                className="w-full flex items-center gap-3 border border-[#FF3B30]/40 bg-gradient-to-r from-[#FF3B30]/[0.08] to-transparent pl-4 pr-3 py-3 hover:bg-[#FF3B30]/[0.14] transition-colors text-left">
                <div className="grid place-items-center h-9 w-9 shrink-0 border border-[#FF3B30]/40 bg-[#FF3B30]/10">
                  <Flag className="h-4 w-4 text-[#FF3B30]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FF3B30]">
                    {dailyRow?.isRace ? "Race Day · Open Race Hub" : `T-${dailyRow?.daysUntilRace} · Open Race Hub`}
                  </p>
                  <p className="text-sm font-display uppercase text-white tracking-tight leading-tight truncate">
                    Countdown · morning meal · in-race fuelling · checklist
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 shrink-0" />
              </button>
            )}

            {/* Vs-last-week deltas — kcal / carbs / protein / fat. Shown
                every day (not just Sundays) per beta feedback so athletes
                see running progress from Monday onward. */}
            {hasTracker && <WeekComparisonChip date={date} />}


            {/* Meals — your plan + what you actually ate, combined */}
            <Section icon={Utensils} title="Meals" subtitle="Log everything you eat against today's plan" accent="#FF9500" testid="tracker-meals"
              right={plannedMeals.length > 0 ? (
                <span data-testid="cal-progress" className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${doneCount === plannedMeals.length ? "border-[#34C759] text-[#34C759] bg-[#34C759]/10" : "border-white/15 text-zinc-300"}`}>{doneCount}/{plannedMeals.length} done</span>
              ) : null}>

              {hasTracker && !target && (
                <div className="border border-white/10 bg-[#141414] p-4 mb-3" data-testid="tracker-summary">
                  <p className="text-[11px] text-zinc-400">Log anything you eat below — no plan needed. Build a plan to compare against daily targets.</p>
                </div>
              )}

              {/* Quick Log strip removed per Feb 2026 UX decision — the
                  Favourites + Recent tabs inside the Add Food dialog
                  cover the same one-tap re-log flow with better
                  discoverability (star icons on every food/meal row). */}

              <div className="space-y-3">
                {mealSlots.map((slot) => (
                  <MealCard key={slot} slot={slot}
                    label={mealSlots.some((k) => k !== slot && baseSlot(k) === baseSlot(slot))
                      ? `${MEAL_LABEL[baseSlot(slot)] === "Snacks" ? "Snack" : MEAL_LABEL[baseSlot(slot)]} ${(String(slot).match(/(\d+)$/) || [, "1"])[1]}`
                      : slotLabel(slot)}
                    planned={plannedBySlot[slot]} done={!!(match && completions[`${match.idx}:${slot}`])}
                    onToggle={() => toggleMeal(slot)} canLog={hasTracker}
                    onSwap={match ? () => openMealSwap(slot, plannedBySlot[slot]) : null}
                    onRemovePlanned={match ? () => removePlannedMeal(slot) : null}
                    onEditPlanned={match && plannedBySlot[slot] ? () => setPathwayEdit({ slot, planned: plannedBySlot[slot] }) : null}
                    entries={hasTracker ? entriesByMeal(slot) : []}
                    onAdd={() => setDialog({ open: true, meal: slot, initialTab: undefined, initialBuildItems: undefined })}
                    onSaveAsMeal={(entries) => setDialog({
                      open: true, meal: slot, initialTab: "meals",
                      initialBuildItems: entries.map((e) => ({
                        name: e.name, qty: e.qty, unit: e.unit,
                        kcal: e.kcal, protein: e.protein, carb: e.carb, fat: e.fat,
                      })),
                    })}
                    onDelete={removeLog} onEdit={editLog} onSwapEntry={setSwapEntryCtx}
                    onToggleFav={toggleFav} quickFavs={quick.favourites || []} token={token} />
                ))}
                {plannedBySlot.fuel && (
                  <div className="border border-[#FFB454]/30 bg-[#141414] flex items-center gap-3 px-3 sm:px-4 py-3 sm:py-4" data-testid="meal-section-fuel"
                    style={{ boxShadow: "inset 3px 0 0 0 #FFB454" }}>
                    <div className="h-[60px] w-[60px] grid place-items-center border border-[#FFB454]/40 bg-[#FFB454]/10 shrink-0">
                      <Utensils className="h-5 w-5 text-[#FFB454]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#FFB454] font-bold truncate">Training Fuel</p>
                      <p className="font-display text-xl sm:text-2xl leading-none text-white mt-1 truncate tabular-nums">
                        {Math.round(plannedBySlot.fuel.kcal)}<span className="text-xs text-zinc-400 ml-1 font-mono">kcal · {Math.round(plannedBySlot.fuel.carb)}g C</span>
                      </p>
                      <p className="text-[10px] font-mono text-zinc-400 mt-1 truncate">{plannedBySlot.fuel.name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {match && (
                        <button data-testid="remove-planned-fuel" onClick={() => removePlannedMeal("fuel")} title="Remove for today"
                          className="p-1.5 border border-white/15 text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <button data-testid="cal-meal-done-fuel" onClick={() => toggleMeal("fuel")}
                        className={`p-1.5 border transition-colors ${match && completions[`${match.idx}:fuel`] ? "border-[#34C759] bg-[#34C759]/20 text-[#34C759]" : "border-white/15 text-zinc-300 hover:text-[#34C759] hover:border-[#34C759]/50"}`}>
                        <Check className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {removedPlanned.length > 0 && (
                  <div className="border border-white/10 bg-black/20 px-4 py-2.5 flex flex-wrap items-center gap-2" data-testid="removed-planned-strip">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-300">Removed today:</span>
                    {removedPlanned.map((m) => (
                      <button key={m.slot} data-testid={`restore-planned-${m.slot}`} onClick={() => restorePlannedMeal(m.slot)}
                        className="inline-flex items-center gap-1 text-[11px] text-zinc-300 border border-white/15 px-2 py-0.5 hover:text-[#34C759] hover:border-[#34C759]/50 transition-colors">
                        <RotateCcw className="h-3 w-3" /> {SLOT_LABEL[m.slot] || m.slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {planDay?.fuelPlan && <p className="text-[11px] text-[#FFB454] mt-3">Big-day fuel top-up: +{planDay.fuelPlan.kcal} kcal · {planDay.fuelPlan.carb}g carbs</p>}

              {!hasTracker && <div className="mt-4"><UpsellCard navigate={navigate} refreshUser={refreshUser} /></div>}
            </Section>

            {hasTracker && (
              <div className="pb-1">
                <button data-testid="ask-ai-today-btn"
                  onClick={() => navigate(`/ask-ai?q=${encodeURIComponent("How am I doing on fuelling today? Anything I should change before bed?")}`)}
                  className="w-full flex items-center justify-center gap-2 border border-[#A78BFA]/50 bg-[#A78BFA]/[0.06] hover:bg-[#A78BFA]/[0.14] text-[#A78BFA] h-11 text-[11px] uppercase tracking-[0.14em] font-bold transition-colors">
                  <Sparkles className="h-4 w-4" /> Ask AI about today
                </button>
              </div>
            )}

            {hasTracker && (
              <Section icon={Sun} title="Daily Snapshot" subtitle="Log your weight and progress photos" accent="#4C9AFF" testid="daily-snapshot" defaultOpen={false}>
              <AtAGlanceGrid
                date={date}
                note={note}
                weightTracking={user?.weightTracking || "weekly"}
                progressPhotosEnabled={!!user?.progressPhotosEnabled}
                progressPhotos={progressPhotos}
                onProgressPhotoUploaded={(p) => setProgressPhotos((cur) => [p, ...cur])}
                onProgressPhotoDeleted={(id) => setProgressPhotos((cur) => cur.filter((x) => x.id !== id))}
                weightKg={day?.weight?.kg || null}
                weightGoalKg={planGoalWeight || match?.plan?.profile?.goalWeight || match?.plan?.profile?.weight || null}
                weightUnit={weightUnit}
                onWeightSave={async (v) => {
                  try {
                    await api.post("/tracker/weight", { date, kg: v || 0 });
                    toast.success(v ? `Weight logged · ${v}kg` : "Weight cleared");
                    loadDay(date); loadHistory();
                  } catch { toast.error("Could not save weight"); }
                }}
              />
              </Section>
            )}

            {/* Weekly Sunday check-in — only appears on Sundays so athletes see
                it once a week when the whole-week reflection is due. */}


            <JournalSection only="training" note={note} sessions={sessions} tpToday={tpToday} tpConnected={tp.connected}
              hasPlan={!!match} updateNote={updateNote} toggleTag={toggleTag} saveNote={saveNote}
              savingNote={savingNote} noteDirty={noteDirty} onRefreshTp={refreshTp} tpRefreshing={tpSyncing}
              onEditTraining={() => navigate("/app")}
              tpDiff={tpDiff}
              onTpResync={resyncFromTp}
              tpResyncing={tpResyncing}
              weightKg={day?.weight?.kg || match?.plan?.profile?.weight || null}
              unitSystem={unitSystem} />

            {/* Weekly vitamins & minerals for the active plan — Pro-tier only.
                - Meal planning ON (pathways) → full personalised WeeklyMicros rollup
                - Meal planning OFF → NHS RDA reference table (recommended intakes only) */}
            {!isLite && activePlan?.mealPlanning !== false && micros && (
              <Section icon={Sparkles} title="Vitamins & Minerals" subtitle="Micros from everything you've logged, with gaps flagged" accent="#FB7185" testid="tracker-micros">
                <WeeklyMicros micros={micros} />
              </Section>
            )}
            {!isLite && activePlan?.mealPlanning === false && (
              <Section icon={Sparkles} title="Vitamins & Minerals" subtitle="Log your meals to see today's micronutrients" accent="#FB7185" testid="tracker-micros-off">
                <VitaminsReference profile={activePlan?.profile} />
              </Section>
            )}

            {/* Hydration + Bodyweight now live as tap-to-edit cards inside the
                At-a-Glance grid above — no duplicate section needed here. */}

            {/* Weekly / daily viz has moved to /insights. Add a shortcut so
                athletes see where to go to reflect. */}
            {hasTracker && (
              <button data-testid="tracker-insights-link" onClick={() => navigate("/insights")}
                className="group w-full text-left flex items-center gap-4 border border-white/10 bg-[#141414] hover:border-[#FF9500]/40 hover:bg-white/[0.03] transition-colors px-4 sm:px-5 py-4">
                <span className="grid place-items-center h-12 w-12 shrink-0 bg-[#FF9500]" style={{ boxShadow: "0 8px 24px -14px #FF9500" }}>
                  <LineChart className="h-6 w-6 text-black" strokeWidth={2.2} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold uppercase tracking-[0.2em] text-white leading-tight break-words">Insights</span>
                  <span className="block text-xs text-zinc-300 mt-1 leading-snug break-words">Trends, streaks &amp; PBs from everything you log</span>
                </span>
                <ArrowRight className="h-4 w-4 text-[#FF9500] shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}
          </div>
        )}
      </main>

      <FoodLogDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        date={date} meal={dialog.meal} planMeals={day?.planMeals || []}
        initialTab={dialog.initialTab} initialBuildItems={dialog.initialBuildItems}
        onEditPlanMeal={(m) => setPathwayEdit({ slot: m.slot || dialog.meal || "snack", planned: m })}
        onAdded={() => { loadDay(date); loadHistory(); loadQuick(); }} />

      {/* Full pathway-meal edit dialog — opens when the athlete taps the
          yellow "Edit" button on a planned meal. Ingredient-level swap,
          add-extra and portion tweak before logging. */}
      <PathwayMealEditDialog
        open={!!pathwayEdit}
        onOpenChange={(o) => !o && setPathwayEdit(null)}
        date={date}
        meal={pathwayEdit?.slot}
        planned={pathwayEdit?.planned}
        onLogged={() => { loadDay(date); loadHistory(); }} />

      <Dialog open={!!swapCtx} onOpenChange={(o) => !o && setSwapCtx(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/15 text-white max-w-lg" data-testid="tracker-swap-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-tight">
              Swap {swapCtx ? slotLabel(swapCtx.slot) : ""}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Pick a different meal for this slot — portions rebalance to hit the day's targets.
            </DialogDescription>
          </DialogHeader>
          {swapLoading ? (
            <div className="py-8 grid place-items-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : swapAlts.length === 0 ? (
            <p className="py-6 text-sm text-zinc-400 text-center">No alternatives available for this slot.</p>
          ) : (
            <div className="max-h-[24rem] overflow-y-auto space-y-2 pr-1">
              {swapAlts.map((a) => (
                <button key={a.id} data-testid={`swap-option-${a.id}`} disabled={swapping}
                  onClick={() => doMealSwap(a.id)}
                  className="w-full text-left border border-white/10 bg-[#141414] hover:border-[#E2E30A]/50 hover:bg-white/[0.03] p-3 transition-colors disabled:opacity-50">
                  <p className="text-sm text-white">{a.name}</p>
                  <p className="text-[10px] font-mono text-zinc-400 mt-0.5">{Math.round(a.kcal)}kcal · {Math.round(a.protein)}P {Math.round(a.carb)}C {Math.round(a.fat)}F</p>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fine-grained edit dialog — replaces the old window.prompt so
          athletes can dial in the exact quantity/unit AND override any macro
          value individually (per beta-tester feedback). */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent data-testid="edit-entry-dialog"
          className="force-dark bg-[#141414] border-white/15 text-white sm:max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle className="text-xs font-bold uppercase tracking-[0.22em]">Edit entry</DialogTitle>
            <DialogDescription className="sr-only">Update quantity and macros for this logged food.</DialogDescription>
          </DialogHeader>
          {editEntry && <EditEntryForm entry={editEntry} onSave={saveEditEntry} onCancel={() => setEditEntry(null)} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!swapEntryCtx} onOpenChange={(o) => !o && setSwapEntryCtx(null)}>
        <DialogContent data-testid="swap-entry-dialog"
          className="force-dark bg-[#141414] border-white/15 text-white sm:max-w-md rounded-none p-0 gap-0 max-h-[85vh] flex flex-col">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/10">
            <DialogTitle className="text-xs font-bold uppercase tracking-[0.22em]">Swap ingredient</DialogTitle>
            <DialogDescription className="text-xs text-zinc-400 mt-1">
              Replace <span className="text-white">{swapEntryCtx?.name}</span> with a different food — macros will be rescaled to the same weight.
            </DialogDescription>
          </DialogHeader>
          {swapEntryCtx && <SwapEntrySearch onPick={swapEntryToFood} onCancel={() => setSwapEntryCtx(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small food-search dialog body used to swap an already-logged row.
// Queries /tracker/search with debouncing; on pick, hands the food back
// up so the parent PATCHes the entry with fresh macros scaled to the row's
// existing grams. Cancel resets to the original row.
function SwapEntrySearch({ onPick, onCancel }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/tracker/search?q=${encodeURIComponent(term)}`)
        .then(({ data }) => { if (!cancelled) setResults((data && data.results) || []); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);
  const pick = async (food) => {
    setSaving(true);
    try { await onPick(food); } finally { setSaving(false); }
  };
  return (
    <>
      <div className="px-5 pt-4 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2 border border-white/15 bg-[#0F0F0F] px-2 py-1.5">
          <Search className="h-3.5 w-3.5 text-zinc-400" />
          <input autoFocus data-testid="swap-entry-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search 'semi-skimmed milk', 'whey protein'…"
            className="flex-1 bg-transparent border-0 outline-none text-sm text-white placeholder:text-zinc-500" />
          {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
        </div>
      </div>
      <div className="overflow-y-auto flex-1">
        {!q.trim() && (
          <p className="text-xs text-zinc-400 px-5 py-6 text-center">Type at least 2 letters. Search covers the SPC food database and Open Food Facts.</p>
        )}
        {q.trim() && !searching && results.length === 0 && (
          <p className="text-xs text-zinc-400 px-5 py-6 text-center">No matches — try a broader term.</p>
        )}
        {results.map((food, i) => (
          <button key={food.code || food.name + i} data-testid={`swap-result-${i}`}
            disabled={saving}
            onClick={() => pick(food)}
            className="w-full text-left px-5 py-2.5 border-b border-white/5 hover:bg-white/[0.03] transition-colors disabled:opacity-50">
            <p className="text-sm text-white truncate">{food.name}</p>
            <p className="text-[10px] font-mono text-zinc-400 mt-0.5">
              {food.brand ? <span className="text-zinc-300">{food.brand} · </span> : null}
              {Math.round(food.kcal || 0)} kcal · P {Math.round((food.protein || 0) * 10) / 10} · C {Math.round((food.carb || 0) * 10) / 10} · F {Math.round((food.fat || 0) * 10) / 10} per 100g
            </p>
          </button>
        ))}
      </div>
      <div className="border-t border-white/10 px-5 py-3 bg-[#0F0F0F] flex gap-2">
        <Button data-testid="swap-cancel" onClick={onCancel} variant="outline"
          className="flex-1 rounded-none border-white/15 text-white hover:bg-white/5">Cancel</Button>
      </div>
    </>
  );
}

function EditEntryForm({ entry, onSave, onCancel }) {  const [qty, setQty] = useState(String(entry.qty ?? ""));
  const [time, setTime] = useState(entry.time_local || "");
  const [kcal, setKcal] = useState(String(Math.round(entry.kcal || 0)));
  const [protein, setProtein] = useState(String(entry.protein ?? ""));
  const [carb, setCarb] = useState(String(entry.carb ?? ""));
  const [fat, setFat] = useState(String(entry.fat ?? ""));
  const [saving, setSaving] = useState(false);
  const [qtyDirty, setQtyDirty] = useState(false);
  // When the athlete only edits qty, the backend auto-scales macros — keeps
  // the form honest by echoing the scaled values live so they see the result
  // before hitting save.
  useEffect(() => {
    if (!qtyDirty) return;
    const oldQty = Number(entry.qty) || 0;
    const newQty = parseFloat(qty);
    if (!isFinite(newQty) || oldQty <= 0) return;
    const s = newQty / oldQty;
    setKcal(String(Math.round((entry.kcal || 0) * s)));
    setProtein(String(+((entry.protein || 0) * s).toFixed(1)));
    setCarb(String(+((entry.carb || 0) * s).toFixed(1)));
    setFat(String(+((entry.fat || 0) * s).toFixed(1)));
  }, [qty, qtyDirty, entry]);

  const submit = async () => {
    const patch = {};
    if (qty !== "" && qty !== String(entry.qty)) patch.qty = parseFloat(qty);
    if (Number(kcal) !== Math.round(entry.kcal || 0)) patch.kcal = parseFloat(kcal);
    if (parseFloat(protein) !== Number(entry.protein)) patch.protein = parseFloat(protein);
    if (parseFloat(carb) !== Number(entry.carb)) patch.carb = parseFloat(carb);
    if (parseFloat(fat) !== Number(entry.fat)) patch.fat = parseFloat(fat);
    if (time !== (entry.time_local || "")) patch.time = time;
    if (!Object.keys(patch).length) { onCancel(); return; }
    setSaving(true);
    try { await onSave(patch); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-200 truncate">{entry.name}</p>
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-1.5">Quantity</p>
        <div className="flex items-center gap-2">
          <Input data-testid="edit-entry-qty" type="number" inputMode="decimal" step="0.1" value={qty}
            onChange={(e) => { setQty(e.target.value); setQtyDirty(true); }}
            className="flex-1 bg-black border-white/15 text-white text-lg font-display h-11" autoFocus />
          <span className="text-sm font-mono uppercase tracking-wider text-zinc-400 shrink-0 w-14">{entry.unit || "g"}</span>
        </div>
        <p className="text-[10px] text-zinc-500 mt-1">Macros auto-scale when quantity changes — override any value below.</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-1.5">Time eaten</p>
        <TimeField value={time} onChange={setTime} testid="edit-entry-time" />
        <p className="text-[10px] text-zinc-500 mt-1">Used for your Eating Times trends in Insights.</p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[
          ["kcal", "kcal", kcal, setKcal, "1"],
          ["protein", "P", protein, setProtein, "0.1"],
          ["carb", "C", carb, setCarb, "0.1"],
          ["fat", "F", fat, setFat, "0.1"],
        ].map(([k, lbl, val, set, step]) => (
          <div key={k}>
            <label className="text-[10px] uppercase text-zinc-400">{lbl}</label>
            <Input data-testid={`edit-entry-${k}`} type="number" min="0" step={step} value={val}
              onChange={(e) => set(e.target.value)}
              className="bg-black border-white/15 text-white h-10" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" data-testid="edit-entry-save" onClick={submit} disabled={saving}
          className="flex-1 inline-flex items-center justify-center gap-1.5 border-2 border-[#E2E30A] bg-[#E2E30A] text-black font-bold uppercase tracking-wider text-xs px-3 py-2.5 transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> Save</>}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2.5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 text-xs uppercase tracking-wider transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

function MealCard({ slot, label, planned, done, onToggle, onSwap, onRemovePlanned, onEditPlanned, canLog, entries, onAdd, onSaveAsMeal, onDelete, onEdit, onSwapEntry, onToggleFav, quickFavs = [], token }) {
  const kcLogged = entries.reduce((s, e) => s + (e.kcal || 0), 0);
  const mProtLogged = entries.reduce((s, e) => s + (e.protein || 0), 0);
  const mCarbLogged = entries.reduce((s, e) => s + (e.carb || 0), 0);
  const mFatLogged  = entries.reduce((s, e) => s + (e.fat || 0), 0);
  // Beta feedback (Bethan, Feb 26 2026): "when I select the pre-planned
  // lunch as done it doesnt change the nutrition info for that meal, but
  // does account for it in the daily overall." Root cause: the day's
  // consumed totals include planned meals marked done, but the per-meal
  // card only read from `entries` so it showed 0g. Fix: when a planned
  // meal is ticked done AND nothing has been logged, credit the planned
  // macros to the per-meal display so it matches the daily overall.
  const creditPlanned = !!(done && planned && entries.length === 0);
  const kc = kcLogged + (creditPlanned ? (planned.kcal || 0) : 0);
  const mProt = mProtLogged + (creditPlanned ? (planned.protein || 0) : 0);
  const mCarb = mCarbLogged + (creditPlanned ? (planned.carb || 0) : 0);
  const mFat  = mFatLogged  + (creditPlanned ? (planned.fat || 0) : 0);
  const [open, setOpen] = React.useState(false);
  const hasContent = !!planned || (entries && entries.length > 0);
  // Garmin-style completion ring — reflects kcal logged vs. planned kcal (if
  // we have a plan for this slot) or fills to 100% once anything is logged
  // when there's no plan. Keeps the At-a-Glance grid's visual language flowing
  // through the meal list.
  const plannedKcal = planned?.kcal || 0;
  const ringPct = plannedKcal > 0
    ? Math.max(0, Math.min(100, (kc / plannedKcal) * 100))
    : (entries.length ? Math.min(100, entries.length * 33) : 0);
  const slotColor = slot === "breakfast" ? "#FFB454" : slot === "lunch" ? "#E2E30A" : slot === "dinner" ? "#FF6A3D" : "#4C9AFF";
  const rc = 2 * Math.PI * 15; // r=15
  const ringDash = (ringPct / 100) * rc;
  return (
    <div className="border border-white/10 bg-[#141414]" data-testid={`meal-section-${slot}`}
      style={{ boxShadow: `inset 3px 0 0 0 ${slotColor}` }}>
      <div className="flex items-center gap-3 px-3 sm:px-4 py-3 sm:py-4">
        <button type="button" onClick={() => setOpen((v) => !v)}
          data-testid={`meal-toggle-${slot}`}
          className="flex-1 min-w-0 flex items-center gap-3 text-left hover:opacity-90">
          <div className="relative shrink-0" style={{ width: 60, height: 60 }} data-testid={`meal-ring-${slot}`}>
            <svg width="60" height="60" className="-rotate-90">
              <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
              <circle cx="30" cy="30" r="26" fill="none" stroke={slotColor} strokeWidth="5"
                strokeDasharray={`${(ringPct / 100) * (2 * Math.PI * 26)} ${(2 * Math.PI * 26) - (ringPct / 100) * (2 * Math.PI * 26)}`}
                strokeLinecap="round"
                style={{ transition: "stroke-dasharray 0.6s ease-out" }} />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
              <Utensils className="h-4 w-4" style={{ color: slotColor }} />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold truncate">{label}</p>
            <p className="font-display text-xl sm:text-2xl leading-none text-white mt-1 truncate tabular-nums">
              {Math.round(kc)}<span className="text-xs text-zinc-400 ml-1 font-mono">{plannedKcal ? `/ ${Math.round(plannedKcal)} kcal` : "kcal"}</span>
            </p>
            <p className="text-[10px] font-mono text-zinc-400 mt-1 truncate">
              {planned ? planned.name : (entries.length ? `${entries.length} item${entries.length > 1 ? "s" : ""} logged` : "Tap to log")}
              {creditPlanned && <span className="text-[#34C759] ml-1">· done</span>}
            </p>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {/* Single unified LOG button — always opens fresh FoodLogDialog so
              athletes can log whatever they actually ate. The planned meal
              stays visible below as a reference guide. */}
          {canLog && (
            <button data-testid={`log-food-${slot}`} onClick={(e) => { e.stopPropagation(); onAdd(); }}
              title="Log what you ate — search, barcode, photo or build a meal"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-[#E2E30A] bg-[#E2E30A]/15 hover:bg-[#E2E30A]/25 border border-[#E2E30A]/40 px-3 py-2 transition-colors">
              <Plus className="h-3.5 w-3.5" /> Log
            </button>
          )}
          <button type="button" onClick={() => setOpen((v) => !v)}
            className={`p-1.5 border transition-colors ${hasContent ? "border-white/15 text-zinc-300 hover:text-white hover:border-white/30" : "border-transparent text-transparent pointer-events-none"}`}>
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {/* Per-meal macro breakdown — 3 compact cards (C / P / F) side-by-side.
          Replaced the earlier full-width bars per beta feedback: the
          progress fill looked like 100% at a glance even when the true
          value was ~85%, so cards with big numbers read cleaner. */}
      {(entries.length > 0 || planned) && (
        <div data-testid={`meal-macros-${slot}`} className="px-3 sm:px-4 pb-3 grid grid-cols-3 gap-2">
          {[
            ["Carbs", mCarb, planned?.carb || 0, "#34C759"],
            ["Protein", mProt, planned?.protein || 0, "#4C9AFF"],
            ["Fat", mFat, planned?.fat || 0, "#FFB454"],
          ].map(([label, val, tgt, color]) => {
            const pct = tgt > 0 ? Math.round((val / tgt) * 100) : null;
            const overshoot = tgt > 0 && val > tgt * 1.05; // >5% over
            const under = tgt > 0 && val < tgt * 0.85;     // <85%
            const pctColor = overshoot ? "#FF3B30" : under ? "#FFB454" : color;
            return (
              <div key={label} data-testid={`meal-macro-${slot}-${label.toLowerCase()}`}
                className="border p-2 bg-black/40 relative overflow-hidden"
                style={{ borderColor: `${color}40` }}>
                <div className="flex items-center gap-1 mb-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                  <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color }}>{label}</span>
                </div>
                <p className="font-display text-lg leading-none tabular-nums text-white">
                  {Math.round(val)}<span className="text-[10px] text-zinc-500 font-mono ml-0.5">g</span>
                </p>
                {tgt > 0 ? (
                  <p className="text-[10px] font-mono text-zinc-400 mt-0.5">
                    of {Math.round(tgt)}g · <span style={{ color: pctColor }} className="font-bold">{pct}%</span>
                  </p>
                ) : (
                  <p className="text-[10px] font-mono text-zinc-500 mt-0.5">logged</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && planned && (
        <div className={`px-4 py-3 border-b border-white/5 ${done ? "bg-[#34C759]/[0.06]" : ""}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-zinc-300">Planned</p>
              <p className={`text-sm ${done ? "text-[#34C759]" : "text-white"}`}>{planned.name}</p>
              {Array.isArray(planned.ingredients) && planned.ingredients.length > 0 && (
                <ul className="mt-1 space-y-0.5" data-testid={`planned-ingredients-${slot}`}>
                  {planned.ingredients.map((ing, i) => (
                    <li key={`${ing.name}-${i}`} className="text-[11px] text-zinc-300 leading-snug flex justify-between gap-2">
                      <span className="min-w-0 truncate">{ing.name}</span>
                      <span className="text-[#E2E30A] font-mono shrink-0">{ing.qty}{ing.unit}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[10px] font-mono text-zinc-300 mt-1">{Math.round(planned.kcal)}kcal · {Math.round(planned.protein)}P {Math.round(planned.carb)}C {Math.round(planned.fat)}F</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {onEditPlanned && !done && (
                <button data-testid={`swap-meal-${slot}`} onClick={onEditPlanned} title="Edit ingredients · swap or change qty"
                  className="p-1.5 border border-white/15 text-zinc-300 hover:text-[#E2E30A] hover:border-[#E2E30A]/50 transition-colors">
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
              {onRemovePlanned && (
                <button data-testid={`remove-planned-${slot}`} onClick={onRemovePlanned} title="Remove this planned meal for today"
                  className="p-1.5 border border-white/15 text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <button data-testid={`cal-meal-done-${slot}`} onClick={onToggle} title={done ? "Mark as not eaten" : "Mark as done"}
                className={`p-1.5 border transition-colors ${done ? "border-[#34C759] bg-[#34C759]/20 text-[#34C759]" : "border-white/15 text-zinc-300 hover:text-[#34C759] hover:border-[#34C759]/50"}`}>
                <Check className="h-4 w-4" />
              </button>
            </div>
          </div>
          {/* Prominent primary action — matches the beta ask: "Let me
              edit / swap / adjust qty on ALL ingredients in this meal
              from one place." Opens PathwayMealEditDialog. */}
          {onEditPlanned && !done && (
            <button
              type="button"
              data-testid={`edit-ingredients-${slot}`}
              onClick={onEditPlanned}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-bold text-[#E2E30A] bg-[#E2E30A]/10 hover:bg-[#E2E30A]/20 border border-[#E2E30A]/40 hover:border-[#E2E30A]/70 px-3 py-2.5 transition-colors">
              <Pencil className="h-3.5 w-3.5" />
              Edit ingredients · swap · change qty
            </button>
          )}
        </div>
      )}

      {open && canLog ? (
        entries.length === 0 ? (
          <p className="px-4 py-3 text-xs text-zinc-300">Nothing logged yet.</p>
        ) : (
          <>
            <ul className="divide-y divide-white/5">
              {entries.map((e) => {
                const favMatch = (quickFavs || []).find((f) => f.name && e.name && f.name.toLowerCase() === e.name.toLowerCase());
                const isFav = !!favMatch;
                return (
                <li key={e.id} data-testid={`log-entry-${e.id}`} className="flex items-center gap-3 px-4 py-2.5">
                  {e.photo_path && <img src={`${API}/tracker/photo/${e.photo_path}?auth=${token}`} alt="" className="h-10 w-10 object-cover rounded shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{e.name}{e.qty && (e.unit === "g" || e.unit === "ml") ? ` · ${e.qty}${e.unit}` : ""}</p>
                    <p className="text-[10px] font-mono text-zinc-400">
                      {e.time_local && <span className="text-[#4C9AFF]">{e.time_local} · </span>}
                      {Math.round(e.kcal)}kcal · {e.protein}P {e.carb}C {e.fat}F <span className="text-zinc-300">· {e.source}</span>
                    </p>
                  </div>
                  {onToggleFav && (
                    <button data-testid={`fav-entry-${e.id}`}
                      onClick={() => onToggleFav({
                        id: favMatch?.id, name: e.name, meal: slot,
                        qty: e.qty, unit: e.unit,
                        kcal: e.kcal, protein: e.protein, carb: e.carb, fat: e.fat,
                        source: e.source,
                      }, isFav)}
                      className={`p-1.5 transition-colors ${isFav ? "text-[#E2E30A]" : "text-zinc-300 hover:text-[#E2E30A]"}`}
                      title={isFav ? "Remove from favourites" : "Add to favourites"}>
                      <Star className={`h-4 w-4 ${isFav ? "fill-[#E2E30A]" : ""}`} />
                    </button>
                  )}
                  <button data-testid={`edit-entry-${e.id}`} onClick={() => onEdit(e)} className="p-1.5 text-zinc-300 hover:text-[#E2E30A] transition-colors" title="Edit quantity">
                    <Pencil className="h-4 w-4" />
                  </button>
                  {onSwapEntry && (
                    <button data-testid={`swap-entry-${e.id}`} onClick={() => onSwapEntry(e)} className="p-1.5 text-zinc-300 hover:text-[#E2E30A] transition-colors" title="Swap for a different food">
                      <Repeat className="h-4 w-4" />
                    </button>
                  )}
                  <button data-testid={`delete-entry-${e.id}`} onClick={() => onDelete(e.id)} className="p-1.5 text-zinc-300 hover:text-[#FF3B30] transition-colors" title="Remove"><Trash2 className="h-4 w-4" /></button>
                </li>
                );
              })}
            </ul>
            {entries.length >= 2 && onSaveAsMeal && (
              <div className="px-4 py-2.5 border-t border-white/5 bg-[#0F0F0F]">
                <button type="button" data-testid={`save-as-meal-${slot}`}
                  onClick={() => onSaveAsMeal(entries)}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-[#E2E30A] hover:text-black bg-[#E2E30A]/[0.08] hover:bg-[#E2E30A] border border-[#E2E30A]/40 hover:border-[#E2E30A] px-3 py-2 transition-colors">
                  <Bookmark className="h-3.5 w-3.5" /> Save these {entries.length} items as a meal
                </button>
                <p className="text-[10px] text-zinc-400 mt-1.5 text-center">Reuse them in one tap next time — appears in My Meals.</p>
              </div>
            )}
          </>
        )
      ) : null}
    </div>
  );
}

const UPSELL_FEATURES = [
  "Log everything you eat by search, barcode or AI food photo",
  "Live macro & hydration bars against your plan's daily targets",
  "Insights: calories vs target, adherence %, hydration, bodyweight & training-load graphs",
];

function UpsellCard({ navigate, refreshUser }) {
  const { trackerTrialUsed } = useAuth();
  const [adding, setAdding] = useState(false);
  const addTracker = async () => {
    setAdding(true);
    try {
      await api.post("/payments/add-tracker");
      await refreshUser();
      toast.success("Tracker unlocked — enjoy!");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) { toast.message("Choose a plan to add the Tracker"); navigate("/subscribe"); }
      else { toast.error(e?.response?.data?.detail || "Could not add the Tracker. Please try again."); setAdding(false); }
    }
  };
  const startTrial = async () => {
    setAdding(true);
    try {
      await api.post("/payments/start-tracker-trial");
      await refreshUser();
      toast.success("3-day Tracker trial started — enjoy!");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) { toast.message("Choose a plan to start the trial"); navigate("/subscribe"); }
      else { toast.error(e?.response?.data?.detail || "Could not start your trial. Please try again."); setAdding(false); }
    }
  };
  return (
    <section data-testid="tracker-upsell" className="border border-[#4C9AFF]/40 bg-[#4C9AFF]/[0.06] p-6 text-center">
      <div className="inline-flex items-center justify-center h-14 w-14 border border-[#4C9AFF]/40 bg-[#4C9AFF]/10 mb-4">
        <Lock className="h-6 w-6 text-[#4C9AFF]" />
      </div>
      <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tighter">Unlock food logging & insights</h2>
      <p className="text-zinc-300 mt-3 text-sm max-w-md mx-auto">Your training, planned meals and notes are always free. Add the Tracker to log what you actually eat and see your progress in graphs.</p>
      <ul className="mt-6 space-y-2 text-left max-w-sm mx-auto">
        {UPSELL_FEATURES.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-zinc-200"><Check className="h-4 w-4 text-[#4C9AFF] shrink-0 mt-0.5" /> {f}</li>
        ))}
      </ul>
      {!trackerTrialUsed ? (
        <>
          <p className="font-display text-3xl sm:text-4xl tracking-tighter text-[#4C9AFF] mt-6">3 days free</p>
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 mt-1">No card needed · then £4.99/mo only if you keep it</p>
          <Button data-testid="trial-tracker-btn" onClick={startTrial} disabled={adding}
            className="mt-4 w-full sm:w-auto h-12 px-8 rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black font-semibold uppercase tracking-wide transition-colors">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start 3-Day Free Trial"}
          </Button>
        </>
      ) : (
        <>
          <p className="font-display text-4xl tracking-tighter text-[#4C9AFF] mt-6">£4.99<span className="text-sm text-zinc-400"> /month</span></p>
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 mt-1">Added to your subscription · cancel anytime</p>
          <Button data-testid="add-tracker-btn" onClick={addTracker} disabled={adding}
            className="mt-4 w-full sm:w-auto h-12 px-8 rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black font-semibold uppercase tracking-wide transition-colors">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Tracker"}
          </Button>
        </>
      )}
    </section>
  );
}
